import React, { useState, useEffect } from 'react';
import { Button, Typography, Card, Space, Tag, Timeline, Alert, Spin, Grid } from 'antd';
import { 
    ClockCircleOutlined, 
    CheckCircleOutlined, 
    ExclamationCircleOutlined,
    UserOutlined,
    ReloadOutlined
} from '@ant-design/icons';
import { SelfRegistrationSessionDTO } from '@/shared/api/model';
import { useGetSession } from '@/shared/api/client/self-registration';
import { showSuccessNotification, showErrorNotification } from '@/components/common/utils/notify';

const { Title, Text, Paragraph } = Typography;
const { useBreakpoint } = Grid;

interface OfficerApprovalStepProps {
    session: SelfRegistrationSessionDTO;
    onComplete: (session: SelfRegistrationSessionDTO) => void;
    onError: (message: string) => void;
}

const OfficerApprovalStep: React.FC<OfficerApprovalStepProps> = ({
    session,
    onComplete,
    onError
}) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const screens = useBreakpoint();
    const isMobile = !screens.md;

    // Use the session query hook directly at component level
    const sessionQuery = useGetSession(session.sessionToken || '', {
        query: {
            enabled: !!session.sessionToken,
            refetchInterval: 30000, // Auto-refresh every 30 seconds
            staleTime: 10000 // Consider data stale after 10 seconds
        }
    });

    // Clear error when component mounts or session changes
    useEffect(() => {
        setError(null);
    }, [session.sessionToken]);

    // Handle session updates from the query
    useEffect(() => {
        if (sessionQuery.data?.data) {
            const sessionData = sessionQuery.data.data;
            const actualSessionData = (sessionData as any)?.data || sessionData;
            
            // Check if status has changed to approved or rejected
            if (actualSessionData?.officerApproved || actualSessionData?.status === 'REJECTED') {
                // Clear any existing errors since we have a status update
                setError(null);
                onComplete(actualSessionData);
            }
        }
        
        if (sessionQuery.error) {
            const errorMessage = 'Failed to refresh status';
            setError(errorMessage);
            onError(errorMessage);
        }
    }, [sessionQuery.data, sessionQuery.error, onComplete, onError]);

    const handleRefreshStatus = () => {
        // Clear any existing errors
        setError(null);
        
        // Trigger a manual refetch
        sessionQuery.refetch().then(() => {
            showSuccessNotification('Status refreshed');
        }).catch((error: any) => {
            const errorMessage = error.message || 'Failed to refresh status';
            setError(errorMessage);
            onError(errorMessage);
        });
    };

    const getStatusColor = () => {
        if (session.status === 'REJECTED') return 'red';
        if (session.officerApproved) return 'green';
        return 'orange';
    };

    const getStatusText = () => {
        if (session.status === 'REJECTED') return 'Application Rejected';
        if (session.officerApproved) return 'Application Approved';
        return 'Pending Review';
    };

    const getStatusIcon = () => {
        if (session.status === 'REJECTED') return <ExclamationCircleOutlined />;
        if (session.officerApproved) return <CheckCircleOutlined />;
        return <ClockCircleOutlined />;
    };

    if (session.officerApproved) {
        return (
            <div style={{ textAlign: 'center' }}>
                <CheckCircleOutlined style={{ 
                    fontSize: isMobile ? '36px' : '48px', 
                    color: '#52c41a', 
                    marginBottom: isMobile ? '12px' : '16px' 
                }} />
                <Title level={isMobile ? 4 : 3} style={{ color: '#52c41a' }}>Application Approved!</Title>
                <Text type="secondary" style={{ 
                    fontSize: isMobile ? '14px' : '16px',
                    display: 'block'
                }}>
                    Your registration has been approved by our officer. You can now complete the registration process.
                </Text>
                
                {session.reviewComments && (
                    <Card 
                        style={{ marginTop: isMobile ? '16px' : '20px', textAlign: 'left' }}
                        size={isMobile ? 'small' : 'default'}
                    >
                        <Text strong style={{ fontSize: isMobile ? '14px' : '16px' }}>Officer Comments:</Text>
                        <Paragraph style={{ 
                            marginTop: '8px',
                            fontSize: isMobile ? '13px' : '14px'
                        }}>
                            {session.reviewComments}
                        </Paragraph>
                    </Card>
                )}

                <Button
                    type="primary"
                    size={isMobile ? 'middle' : 'large'}
                    style={{ 
                        marginTop: isMobile ? '20px' : '30px',
                        height: isMobile ? '44px' : '48px',
                        fontSize: isMobile ? '16px' : '18px'
                    }}
                    onClick={() => onComplete({
                        ...session,
                        currentStep: 'COMPLETED' as any
                    })}
                >
                    Complete Registration
                </Button>
            </div>
        );
    }

    if (session.status === 'REJECTED') {
        return (
            <div style={{ textAlign: 'center' }}>
                <ExclamationCircleOutlined style={{ 
                    fontSize: isMobile ? '36px' : '48px', 
                    color: '#ff4d4f', 
                    marginBottom: isMobile ? '12px' : '16px' 
                }} />
                <Title level={isMobile ? 4 : 3} style={{ color: '#ff4d4f' }}>Application Rejected</Title>
                <Text type="secondary" style={{ 
                    fontSize: isMobile ? '14px' : '16px',
                    display: 'block'
                }}>
                    Unfortunately, your registration application has been rejected.
                </Text>
                
                {session.rejectionReason && (
                    <Alert
                        message="Rejection Reason"
                        description={session.rejectionReason}
                        type="error"
                        showIcon
                        // @ts-ignore
                        size={isMobile ? 'small' : 'default'}
                        style={{ marginTop: isMobile ? '16px' : '20px', textAlign: 'left' }}
                    />
                )}

                {session.reviewComments && (
                    <Card 
                        style={{ marginTop: isMobile ? '16px' : '20px', textAlign: 'left' }}
                        size={isMobile ? 'small' : 'default'}
                    >
                        <Text strong style={{ fontSize: isMobile ? '14px' : '16px' }}>Officer Comments:</Text>
                        <Paragraph style={{ 
                            marginTop: '8px',
                            fontSize: isMobile ? '13px' : '14px'
                        }}>
                            {session.reviewComments}
                        </Paragraph>
                    </Card>
                )}

                <Space 
                    style={{ marginTop: isMobile ? '20px' : '30px' }}
                    direction={isMobile ? 'vertical' : 'horizontal'}
                    size={isMobile ? 'middle' : 'large'}
                >
                    <Button 
                        type="primary" 
                        onClick={() => window.location.href = '/contact'}
                        size={isMobile ? 'middle' : 'large'}
                        style={{ 
                            height: isMobile ? '44px' : '48px',
                            fontSize: isMobile ? '16px' : '18px',
                            width: isMobile ? '100%' : 'auto'
                        }}
                    >
                        Contact Support
                    </Button>
                    <Button 
                        onClick={() => window.location.href = '/register'}
                        size={isMobile ? 'middle' : 'large'}
                        style={{ 
                            height: isMobile ? '44px' : '48px',
                            fontSize: isMobile ? '16px' : '18px',
                            width: isMobile ? '100%' : 'auto'
                        }}
                    >
                        Start New Application
                    </Button>
                </Space>
            </div>
        );
    }

    return (
        <div>
            <div style={{ textAlign: 'center', marginBottom: isMobile ? '20px' : '30px' }}>
                <ClockCircleOutlined style={{ 
                    fontSize: isMobile ? '36px' : '48px', 
                    color: '#faad14', 
                    marginBottom: isMobile ? '12px' : '16px' 
                }} />
                <Title level={isMobile ? 4 : 3}>Awaiting Officer Review</Title>
                <Text type="secondary" style={{ 
                    fontSize: isMobile ? '14px' : '16px',
                    display: 'block'
                }}>
                    Your application is currently being reviewed by our officers
                </Text>
            </div>

            {/* Status Card */}
            <Card 
                style={{ marginBottom: isMobile ? '16px' : '20px' }}
                size={isMobile ? 'small' : 'default'}
            >
                <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: isMobile ? 'flex-start' : 'center',
                    flexDirection: isMobile ? 'column' : 'row',
                    gap: isMobile ? '12px' : '0'
                }}>
                    <Space>
                        {getStatusIcon()}
                        <div>
                            <Text strong style={{ fontSize: isMobile ? '14px' : '16px' }}>{getStatusText()}</Text>
                            <br />
                            <Text type="secondary" style={{ fontSize: isMobile ? '11px' : '12px' }}>
                                Submitted on {session.createdAt ? new Date(session.createdAt).toLocaleDateString() : 'Unknown'}
                            </Text>
                        </div>
                    </Space>
                    <Tag 
                        color={getStatusColor()}
                        style={{ fontSize: isMobile ? '12px' : '14px' }}
                    >
                        {getStatusText()}
                    </Tag>
                </div>
            </Card>

            {/* Review Timeline */}
            <Card 
                title="Application Timeline" 
                style={{ marginBottom: isMobile ? '16px' : '20px' }}
                size={isMobile ? 'small' : 'default'}
            >
                <Timeline>
                    <Timeline.Item color="green" dot={<CheckCircleOutlined />}>
                        <Text strong style={{ fontSize: isMobile ? '14px' : '16px' }}>Application Submitted</Text>
                        <br />
                    </Timeline.Item>
                    
                    <Timeline.Item color="green" dot={<CheckCircleOutlined />}>
                        <Text strong style={{ fontSize: isMobile ? '14px' : '16px' }}>Documents Uploaded</Text>
                        <br />
                        <Text type="secondary" style={{ fontSize: isMobile ? '12px' : '14px' }}>All required documents received</Text>
                    </Timeline.Item>
                    
                    <Timeline.Item color="blue" dot={<ClockCircleOutlined />}>
                        <Text strong style={{ fontSize: isMobile ? '14px' : '16px' }}>Under Review</Text>
                        <br />
                        <Text type="secondary" style={{ fontSize: isMobile ? '12px' : '14px' }}>Officer reviewing your application</Text>
                    </Timeline.Item>
                    
                    <Timeline.Item color="gray" dot={<UserOutlined />}>
                        <Text strong style={{ fontSize: isMobile ? '14px' : '16px' }}>Decision Pending</Text>
                        <br />
                        <Text type="secondary" style={{ fontSize: isMobile ? '12px' : '14px' }}>Waiting for officer decision</Text>
                    </Timeline.Item>
                </Timeline>
            </Card>

            {/* Information Card */}
            <Alert
                message="What happens next?"
                description={
                    <div>
                        <Paragraph style={{ fontSize: isMobile ? '13px' : '14px' }}>
                            Our officers are currently reviewing your application and uploaded documents. 
                            This process typically takes 1-3 business days.
                        </Paragraph>
                        <Paragraph style={{ fontSize: isMobile ? '13px' : '14px' }}>
                            You will be notified via SMS and email once a decision has been made. 
                            You can also check your application status by returning to this page.
                        </Paragraph>
                        <Paragraph style={{ 
                            marginBottom: 0,
                            fontSize: isMobile ? '13px' : '14px'
                        }}>
                            <Text strong>Reference Number:</Text> {session.sessionToken ? session.sessionToken.substring(0, 8).toUpperCase() : 'N/A'}
                        </Paragraph>
                    </div>
                }
                type="info"
                showIcon
                // @ts-ignore
                size={isMobile ? 'small' : 'default'}
                style={{ marginBottom: isMobile ? '16px' : '20px' }}
            />

            {/* Error Display */}
            {error && (
                <Alert
                    message="Error"
                    description={error}
                    type="error"
                    showIcon
                    closable
                    onClose={() => setError(null)}
                    style={{ marginBottom: isMobile ? '16px' : '20px' }}
                />
            )}

            {/* Refresh Button */}
            <div style={{ textAlign: 'center' }}>
                <Button
                    type="default"
                    icon={sessionQuery.isFetching ? <Spin size="small" /> : <ReloadOutlined />}
                    onClick={handleRefreshStatus}
                    loading={sessionQuery.isFetching}
                    disabled={sessionQuery.isFetching}
                    size={isMobile ? 'middle' : 'large'}
                    style={{ 
                        height: isMobile ? '40px' : '44px',
                        fontSize: isMobile ? '14px' : '16px'
                    }}
                >
                    {sessionQuery.isFetching ? 'Checking Status...' : 'Refresh Status'}
                </Button>
            </div>

            {/* Contact Information */}
            <Card 
                style={{ marginTop: isMobile ? '16px' : '20px', backgroundColor: '#f6ffed' }}
                size={isMobile ? 'small' : 'default'}
            >
                <Text strong style={{ fontSize: isMobile ? '14px' : '16px' }}>Need Help?</Text>
                <Paragraph style={{ 
                    marginBottom: 0, 
                    marginTop: '8px',
                    fontSize: isMobile ? '13px' : '14px'
                }}>
                    If you have questions about your application or need to provide additional information, 
                    please contact our support team at <Text strong>support@hazinasaccoss.co.tz</Text> or
                    call <Text strong>+255 652 859 788</Text>.
                </Paragraph>
            </Card>
        </div>
    );
};

export default OfficerApprovalStep;
